import React, {useEffect} from 'react';
import {View, StyleSheet} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import BoxChampRecalculatingNotice, {
  shouldShowRecalculatingNotice
} from '../scanpack/BoxChampRecalculatingNotice';
import BoxChampPlanErrorNotice from '../scanpack/BoxChampPlanErrorNotice';
import BoxChampOversizeWarning from '../scanpack/BoxChampOversizeWarning';
import BoxChampSetAsideNotice from '../scanpack/BoxChampSetAsideNotice';
import BoxChampHandoffCard from '../scanpack/BoxChampHandoffCard';
import BoxChampOptionsSheet from '../scanpack/BoxChampOptionsSheet';
import BoxChampDimsForm, {getItemsNeedingDims} from '../scanpack/BoxChampDimsForm';
import {boxOptions} from '../scanpack/boxChampOptions';
import {
  setScanPackBarcodeInput,
  setBoxChampState,
  handleBarcodeSubmit,
  confirmBoxChampHandoff,
  dismissBoxChampHandoff,
  showBoxChampHandoff,
  refreshBoxChampBoxes,
  retryBoxChampPlan,
  retryBoxChampCatalog,
  closeBoxChampOptions,
  handleBoxChampSubstitute,
  handleBoxChampCombine,
  handleBoxChampSplit,
  handleBoxChampAddBox,
  handleBoxChampDimsSaveAll,
  clearBoxChampConfirmationTimer
} from '../../actions/scanpackScanningActions';
import {useBoxChampGuided} from './hooks/useBoxChampGuided';
import {isBoxChampEnabled, generalSettingsOf} from './utils/boxChampGuidedSelectors';

const BoxChampGuidedBanners = () => {
  const dispatch = useDispatch();
  const {view, handoff, dismissed, setAsideItems, sealedContents, positionLabel} = useBoxChampGuided();
  const boxChampEnabled = useSelector(isBoxChampEnabled);
  const currentOrder = useSelector(state => state.scanPackScanning.currentOrder);
  const guided = useSelector(state => state.scanPackScanning.boxChamp || {});
  const generalSettings = useSelector(generalSettingsOf);
  const dimsItems = boxChampEnabled ? getItemsNeedingDims(currentOrder?.unscanned_items) : [];

  const dimsModalOpen = Boolean(guided.dimsModalOpen);
  useEffect(() => {
    if (dimsModalOpen && dimsItems.length === 0) dispatch(setBoxChampState({dimsModalOpen: false}));
  }, [dimsModalOpen, dimsItems.length]);

  if (!currentOrder || (!boxChampEnabled && !view)) return null;

  const recalculating =
    guided.recalculating ||
    shouldShowRecalculatingNotice(
      currentOrder,
      boxChampEnabled,
      (currentOrder.scanned_items?.length || 0) > 0
    );

  return (
    <View style={styles.strip}>
      <BoxChampRecalculatingNotice
        visible={recalculating}
        onRefetch={() => dispatch(refreshBoxChampBoxes())}
      />
      <BoxChampPlanErrorNotice
        message={guided.planError}
        onDismiss={() => dispatch(setBoxChampState({planError: ''}))}
        onRetry={() => dispatch(retryBoxChampPlan())}
      />
      <BoxChampOversizeWarning warnings={currentOrder.box_champ_warnings} />
      <BoxChampSetAsideNotice
        message={guided.setAsideMessage}
        tone={guided.setAsideConfirmation ? 'info' : 'warning'}
        onDismiss={() => {
          clearBoxChampConfirmationTimer();
          dispatch(setBoxChampState({setAsideMessage: '', setAsideConfirmation: false}));
        }}
      />
      {view && (
        <BoxChampHandoffCard
          state={handoff}
          onConfirm={() => dispatch(confirmBoxChampHandoff())}
          dismissed={dismissed}
          onClose={() => dispatch(dismissBoxChampHandoff())}
          onShow={() => dispatch(showBoxChampHandoff())}
          onScan={value => {
            dispatch(setScanPackBarcodeInput(value));
            dispatch(handleBarcodeSubmit());
          }}
          message={guided.handoffMessage}
          setAsideItems={setAsideItems}
          sealedContents={sealedContents}
          positionLabel={positionLabel}
        />
      )}
      {view && (
        <BoxChampOptionsSheet
          visible={guided.optionsBoxId != null}
          options={boxOptions({view, boxId: guided.optionsBoxId})}
          catalog={guided.catalog}
          serverError={guided.addBoxError}
          catalogError={guided.catalogError}
          onRetryCatalog={() => dispatch(retryBoxChampCatalog())}
          onSubstitute={boxTypeId => dispatch(handleBoxChampSubstitute(boxTypeId))}
          onCombine={() => dispatch(handleBoxChampCombine())}
          onSplit={boxTypeId => dispatch(handleBoxChampSplit(boxTypeId))}
          onAddBox={payload => dispatch(handleBoxChampAddBox(payload))}
          onClose={() => dispatch(closeBoxChampOptions())}
        />
      )}
      {dimsItems.length > 0 && (
        <BoxChampDimsForm
          key={currentOrder.id}
          testID="boxchamp-dims-form"
          items={dimsItems}
          weightUnit={generalSettings.product_weight_format}
          weightRequired={
            generalSettings.box_champ_weight_check_enabled !== false &&
            Number(generalSettings.box_champ_default_weight || 0) <= 0
          }
          onModalToggle={open => dispatch(setBoxChampState({dimsModalOpen: open}))}
          onSaveAll={payloads => dispatch(handleBoxChampDimsSaveAll(payloads))}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  strip: {
    backgroundColor: '#142130',
    zIndex: 1
  }
});

export default BoxChampGuidedBanners;
